/**
 * @fileoverview Static per-section help content for the Section-editor builder mode.
 * @module ui-admin/help/sectionHelpContent
 *
 * Responsibilities: - Provide a `ComponentHelpEntry` for every document `SectionHelpId` shown in
 *                     the `SectionHelpPopover` (what the section is, how it renders in the PDF,
 *                     configuration tips, and which help-panel section to deep-link to).
 * Dependencies:     - `ComponentHelpEntry`/`SectionHelpId` from `./types`.
 * Architecture:     - Pure static data bundled with the app. No API/Dexie reads, no mock data.
 *                     Local-first by construction — the same illustrated help available offline.
 * Notes:            - The `satisfies Record<SectionHelpId, ComponentHelpEntry>` assertion at the
 *                     bottom is a compile-time exhaustiveness check: adding a new SectionHelpId
 *                     without a help entry (or vice-versa) is a TypeScript error. Mirrors the
 *                     canvas `COMPONENT_HELP` so the two builder modes give the same level of help.
 */

import type { ComponentHelpEntry, SectionHelpId } from './types';

/**
 * @contract SECTION_HELP
 * Purpose: Lookup of just-in-time help content for each document section in Section-editor mode.
 * Rules: Must contain exactly one entry per `SectionHelpId` value (enforced by `satisfies` below).
 *        Every entry has a non-empty `whatItIs`, `rendersAs`, and at least one tip.
 */
export const SECTION_HELP = {
  complexVisitSummary: {
    whatItIs: 'The visit-level summary for a complex inspection report.',
    rendersAs: 'A summary of the complex name, anchor, visit date, and participant counts.',
    tips: ['Keep the summary enabled for complexVisit templates.', 'Standalone counts are shown only when visit data supplies them.'],
    helpSection: 'dynamicComponents',
  },
  complexParticipants: {
    whatItIs: 'The governed participant report section for a complex visit.',
    rendersAs: 'One scoped report block per included governed participant, with an optional standalone summary.',
    tips: ['Use participant flags to control each entity’s detail blocks.', 'Start each entity on a new page when reports are long.'],
    helpSection: 'dynamicComponents',
  },
  cover: {
    whatItIs: 'An optional leading cover page rendered ahead of the document body.',
    rendersAs: 'A full page on its own (logo, title, summary) before the first body page.',
    tips: [
      'Use it for a formal title page or cover letter on longer documents.',
      'Toggle it off for short one-page notices where a cover page would waste paper.',
    ],
    helpSection: 'coverPage',
  },
  header: {
    whatItIs: 'The top-of-page header carrying your department logo, name, address, and report title.',
    rendersAs: 'A branded banner at the top of the document (and optionally repeated on every page).',
    tips: [
      'Logo and department details pull from the department theme so branding stays consistent.',
      'Keep it compact — a tall header pushes body content down on every page.',
      'Add an optional second logo (e.g. a city seal) from the header configuration.',
    ],
    helpSection: 'branding',
  },
  recipientBlock: {
    whatItIs: 'A formatted recipient / property block — owner or tenant name, mailing address, APN, and contacts.',
    rendersAs: 'A left-aligned address block near the top of the body, like the address on a letter.',
    tips: [
      'Fields fill in per case from merge fields such as {{ownerName}} and {{propertyAddress}}.',
      'Place it below the header and above the subject line for a standard letter layout.',
    ],
    helpSection: 'mergeFields',
  },
  subjectLine: {
    whatItIs: 'A bold subject line, e.g. "Re: 123 Main St \u2014 Notice of Violation."',
    rendersAs: 'A single emphasised line introducing the purpose of the document.',
    tips: [
      'Combine static text with a merge field, e.g. "Re: {{propertyAddress}}".',
      'Keep it to one line so it reads as a heading.',
    ],
    helpSection: 'mergeFields',
  },
  salutation: {
    whatItIs: 'An opening greeting line, e.g. "Dear {{ownerName}},".',
    rendersAs: 'A short greeting line at the start of the letter body.',
    tips: [
      'Use a merge field for the name so it personalises automatically.',
      'Word it so it still reads well if the owner name is missing.',
    ],
    helpSection: 'mergeFields',
  },
  bodyText: {
    whatItIs: 'The narrative body — free-form formatted paragraphs that carry the main message.',
    rendersAs: 'Formatted paragraphs exactly as typed, with bold/italic/lists and merge fields preserved.',
    tips: [
      'Type {{ to insert a merge field anywhere in the text.',
      'Use the Advanced mode to bind structured fields from a linked inspection template.',
    ],
    helpSection: 'mergeFields',
  },
  metadata: {
    whatItIs: 'Inspection metadata — compliance status, inspection type, slope, inspector, and dates.',
    rendersAs: 'A compact table or label row of key inspection facts, filled from the inspection record.',
    tips: [
      'This section expands with real inspection data at render time.',
      'Choose which fields appear so the table stays focused on what matters.',
    ],
    helpSection: 'dynamicComponents',
  },
  violations: {
    whatItIs: 'The violations list — a checklist, narrative, or table of cited violations.',
    rendersAs: 'A repeating block that expands with one entry per violation on the inspection.',
    tips: [
      'Pick the layout (checklist / narrative / table) that matches your department\u2019s notices.',
      'Use "Preview with sample data" to see how it expands before publishing.',
    ],
    helpSection: 'dynamicComponents',
  },
  photos: {
    whatItIs: 'Inspection photos with optional captions.',
    rendersAs: 'A grid or list of photos that expands with the photos attached to the inspection.',
    tips: [
      'Set the columns-per-row to balance photo size against page count.',
      'Captions pull from each photo\u2019s description when present.',
    ],
    helpSection: 'dynamicComponents',
  },
  comments: {
    whatItIs: 'Free-text comments and any custom fields captured during the inspection.',
    rendersAs: 'A block of comment text (and custom field values) below the main findings.',
    tips: [
      'Use this for inspector notes that don\u2019t fit the structured sections.',
      'Combine static labels with merge fields for custom captured values.',
    ],
    helpSection: 'mergeFields',
  },
  contactBlock: {
    whatItIs: 'A "for questions, contact the department" block with phone, email, and hours.',
    rendersAs: 'A short contact block, usually near the end of the document.',
    tips: [
      'Contact details pull from the department theme so they stay current.',
      'Keep it brief — one or two lines is enough for most notices.',
    ],
    helpSection: 'branding',
  },
  signatures: {
    whatItIs: 'Signature lines for the inspector and, optionally, the responsible party.',
    rendersAs: 'A closing block with signature spaces, names, titles, and dates near the end of the body.',
    tips: [
      'Use merge fields for the inspector name, title, and date so they fill in per document.',
      'Leave vertical space above each name for a physical or image signature.',
    ],
    helpSection: 'mergeFields',
  },
  legal: {
    whatItIs: 'Legal disclaimers and statutory language, typically set in smaller type.',
    rendersAs: 'A block of fine-print legal text, usually toward the bottom of the document.',
    tips: [
      'Use a smaller font size to set it apart from the main body.',
      'Review the wording with your department before publishing — keep it fixed once approved.',
    ],
    helpSection: 'branding',
  },
  footer: {
    whatItIs: 'A repeating page footer with department info and page numbers.',
    rendersAs: 'A repeating strip at the bottom of every page (e.g. page number, department name).',
    tips: [
      'Footers repeat on every page — keep them short.',
      'Include a page-number token if the document may run to multiple pages.',
    ],
    helpSection: 'branding',
  },
  staticContent: {
    whatItIs: 'A static attachment — pre-made pages (educational guides, reference sheets) appended to the document.',
    rendersAs: 'The attached pages, appended after the generated content of every document.',
    tips: [
      'Use it for content that never changes, like a defensible-space guide.',
      'Static pages are added as-is — they are not affected by inspection data or merge fields.',
    ],
    helpSection: 'overview',
  },
} satisfies Record<SectionHelpId, ComponentHelpEntry>;
